import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import masterListStrings from 'modules/labs/masterList/strings';
import MasterListContainer from 'modules/shared/masterList/containers/MasterListContainer';
import { fetchServicesCatalog, fetchAddServiceSubmission } from 'modules/labs/masterList/actions';
import {
  masterListStatusEnum,
  masterListStatusColorEnum,
  masterListFormValidationErrorType,
} from 'modules/labs/masterList/enums';

// ###############################################################
// <------------------------ Constants -------------------------->
// ###############################################################
const requiredFormFields = ['englishName', 'arabicName', 'servicePrice', 'isSearchable'];
const optionalFormFieldsWithRegexValidation = [];

const requiredFormFieldsWithRegexValidation = {};

// ###############################################################
// <---------------------- End Constants ------------------------>
// ###############################################################

const MasterListView = props => {
  // ###############################################################
  // <------------------------ useState --------------------------->
  // ###############################################################
  const { listQueryStrings } = props;
  const [masterListQueryStrings, setMasterListQueryStrings] = useState(listQueryStrings);
  const [selectedPageFilter, setSelectedPageFilter] = useState(1);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const [formFieldsValues, setFormFieldsValues] = useState({
    englishName: '',
    englishUrl: '',
    englishDescription: '',
    arabicName: '',
    arabicUrl: '',
    arabicDescription: '',
    servicePrice: '',
    isSearchable: true,
    key: '',
  });

  const [formFieldsValidation, setFormFieldsValidation] = useState({
    englishName: masterListFormValidationErrorType.valid,
    englishDescription: masterListFormValidationErrorType.valid,
    arabicName: masterListFormValidationErrorType.valid,
    arabicDescription: masterListFormValidationErrorType.valid,
    servicePrice: masterListFormValidationErrorType.valid,
    isSearchable: masterListFormValidationErrorType.valid,
  });
  // ###############################################################
  // <---------------------- End useState ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ useEffect -------------------------->
  // ###############################################################
  const dispatch = useDispatch();

  // ###############################################################
  // <---------------------- End useEffect ------------------------>
  // ###############################################################
  // ###############################################################
  // <--------------------- rest of hooks ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------- End rest of hooks ---------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Globals --------------------------->
  // ###############################################################
  const servicesList = useSelector(state => state.masterList.ServicesList);
  const totalResultsCount = servicesList?.details?.TotalCount;
  const numberOfPages =
    (totalResultsCount && Math.ceil(totalResultsCount / listQueryStrings.pageSize)) || 1;
  // ###############################################################
  // <----------------------- End Globals ------------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Logic ---------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Logic -------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Actions --------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- End Actions ------------------------->
  // ###############################################################
  // ###############################################################
  // <----------------------- UI Handlers ------------------------->
  // ###############################################################

  const handleLabsSearchBarInputChange = (searchQuery, reset) => {
    setSelectedPageFilter(1);

    const queryString = {
      ...masterListQueryStrings,
    };
    if (searchQuery) queryString.query = searchQuery;
    if (reset) delete queryString.query;

    setMasterListQueryStrings(queryString);

    dispatch(fetchServicesCatalog(queryString));
  };

  const handleMasterListPaginationClick = selectedPageNumber => {
    setSelectedPageFilter(selectedPageNumber);

    const queryString = {
      ...masterListQueryStrings,
    };
    queryString.page = selectedPageNumber;
    dispatch(fetchServicesCatalog(queryString));
  };

  const addServiceSuccessCallback = () => {
    dispatch(fetchServicesCatalog(listQueryStrings));
  };

  const handleConfirmDetailsModalClick = functionCallBack => {
    let isFormValid = true;
    // we get all required fields and then make sure it has a value
    // and if it has a special regex requirement we validate it against that regex
    requiredFormFields.forEach(fieldName => {
      let isFieldValid = masterListFormValidationErrorType.valid;

      if (
        !formFieldsValues[fieldName].toString() ||
        (requiredFormFieldsWithRegexValidation[fieldName] &&
          requiredFormFieldsWithRegexValidation[fieldName].test(formFieldsValues[fieldName]) ===
            false)
      ) {
        isFieldValid =
          (!formFieldsValues[fieldName] && masterListFormValidationErrorType.empty) ||
          masterListFormValidationErrorType.wrong;
        isFormValid = false;
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        [fieldName]: isFieldValid,
      }));
    });

    // we get all optional fields that require specific regix if value is entered
    // and we validate it against that regex
    Object.keys(optionalFormFieldsWithRegexValidation).forEach(key => {
      const fieldValue = formFieldsValues[key];

      let isFieldValid = masterListFormValidationErrorType.valid;
      if (fieldValue && optionalFormFieldsWithRegexValidation[key].test(fieldValue) === false) {
        isFieldValid = masterListFormValidationErrorType.wrong;
        isFormValid = false;
      }

      setFormFieldsValidation(prevState => ({
        ...prevState,
        [key]: isFieldValid,
      }));
    });

    if (isFormValid === false) return;

    const actionBody = {
      addServicePayload: {
        key: formFieldsValues.key,
        ServicePrice: formFieldsValues.servicePrice,
        IsSearchable: formFieldsValues.isSearchable,
        ServiceTranslators: [
          {
            Culture: 'en-US',
            Name: formFieldsValues.englishName,
            Description: formFieldsValues.englishDescription,
            Url: formFieldsValues.englishUrl,
          },
          {
            Culture: 'ar-EG',
            Name: formFieldsValues.arabicName,
            Description: formFieldsValues.arabicDescription,
            Url: formFieldsValues.arabicUrl,
          },
        ],
      },
      callbackAction: addServiceSuccessCallback,
    };
    dispatch(fetchAddServiceSubmission(actionBody));
    setFormFieldsValues({
      englishName: '',
      englishDescription: '',
      arabicName: '',
      arabicDescription: '',
      servicePrice: '',
      isSearchable: true,
      key: '',
      englishUrl: '',
      arabicUrl: '',
    });
    functionCallBack(false);
  };

  const handleCancelDetailsModalClick = functionCallBack => {
    setFormFieldsValidation({
      englishName: masterListFormValidationErrorType.valid,
      englishDescription: masterListFormValidationErrorType.valid,
      arabicName: masterListFormValidationErrorType.valid,
      arabicDescription: masterListFormValidationErrorType.valid,
      servicePrice: masterListFormValidationErrorType.valid,
      isSearchable: masterListFormValidationErrorType.valid,
    });
    setFormFieldsValues({
      englishName: '',
      englishUrl: '',
      englishDescription: '',
      arabicName: '',
      arabicUrl: '',
      arabicDescription: '',
      servicePrice: '',
      isSearchable: true,
      key: '',
    });
    functionCallBack(false);
  };

  const handleMasterListEditButtonClick = masterListDetails => {
    const masterListEnglishDetails = masterListDetails.ServiceTranslators.find(
      details => details.Culture === 'en-US',
    );
    const masterListArabicDetails = masterListDetails.ServiceTranslators.find(
      details => details.Culture === 'ar-EG',
    );

    setFormFieldsValues({
      englishName: masterListEnglishDetails.Name,
      englishDescription: masterListEnglishDetails.Description,
      arabicName: masterListArabicDetails.Name,
      arabicDescription: masterListArabicDetails.Description,
      servicePrice: masterListDetails.ServicePrice,
      isSearchable: masterListDetails.IsSearchable,
      key: masterListDetails.ServiceKey,
      englishUrl: masterListEnglishDetails.Url,
      arabicUrl: masterListArabicDetails.Url,
    });
    setIsDetailsModalOpen(true);
  };
  // ###############################################################
  // <--------------------- End UI Handlers ----------------------->
  // ###############################################################
  // ###############################################################
  // <-------------------------- Render --------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------ End Render ------------------------->
  // ###############################################################
  // ###############################################################
  // <------------------------- Return UI ------------------------->
  // ###############################################################

  return (
    <MasterListContainer
      stringsData={masterListStrings}
      selectedPageFilter={selectedPageFilter}
      numberOfPages={numberOfPages}
      masterListList={servicesList}
      mappedMasterListList={servicesList?.details?.Services || []}
      masterListStatusEnum={masterListStatusEnum}
      masterListStatusColorEnum={masterListStatusColorEnum}
      handleMasterListSearchBarInputChange={handleLabsSearchBarInputChange}
      handleMasterListPaginationClick={handleMasterListPaginationClick}
      masterListQueryStrings={masterListQueryStrings}
      formFieldsValues={formFieldsValues}
      setFormFieldsValues={setFormFieldsValues}
      formFieldsValidation={formFieldsValidation}
      isDetailsModalOpen={isDetailsModalOpen}
      setIsDetailsModalOpen={setIsDetailsModalOpen}
      handleConfirmDetailsModalClick={handleConfirmDetailsModalClick}
      handleCancelDetailsModalClick={handleCancelDetailsModalClick}
      handleMasterListEditButtonClick={handleMasterListEditButtonClick}
      {...props}
    />
  );

  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

MasterListView.propTypes = {
  listQueryStrings: PropTypes.shape({
    pageSize: PropTypes.number,
  }).isRequired,
};

export default MasterListView;
